import React from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, IndianRupee } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const loc = useLocation();
  const from = loc.state?.from || "/dashboard";

  if (!loading && user) return <Navigate to={from} replace />;

  return (
    <div data-testid="login-page" className="mx-auto max-w-6xl px-5 md:px-8 pt-12 pb-24">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        {/* Left: pitch */}
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50/80 px-3 py-1 text-xs font-medium text-blue-700">
            <Sparkles size={14} /> Sign in to take your agent live
          </span>
          <h1 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tight leading-tight text-slate-900">
            One click. <span className="text-blue-600">Your career on autopilot.</span>
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-md">
            Sign in with Google to save your AI agent, claim your founding-member spot, and start earning in ₹ via UPI.
          </p>

          <ul className="mt-6 space-y-3 max-w-md">
            <Bullet icon={ShieldCheck} text="Google sign-in. We never see your password." />
            <Bullet icon={IndianRupee} text="Payouts settle in ₹ to your UPI / bank." />
            <Bullet icon={Sparkles} text="Lifetime ₹0 platform fee on your first gig — founding-member perk." />
          </ul>
        </div>

        {/* Right: card */}
        <div className="lg:justify-self-end w-full max-w-md">
          <div className="tracing-beam rounded-2xl bg-white border border-slate-200 p-8 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.3)]">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">Welcome to Freelancity</p>
            <h2 className="mt-2 font-display text-xl sm:text-2xl font-black text-slate-900">Sign in to continue</h2>
            <p className="mt-2 text-sm text-slate-600">Free forever to join. ₹0 upfront. Cancel anytime.</p>

            <button
              onClick={login}
              data-testid="login-google-btn"
              className="mt-6 w-full inline-flex items-center justify-center gap-3 rounded-md bg-white border border-slate-300 px-4 py-3.5 text-sm font-medium text-slate-800 hover:border-blue-400 hover:bg-blue-50 transition"
            >
              <GoogleIcon /> Continue with Google
            </button>

            <div className="mt-4 flex items-center gap-3 text-xs text-slate-400">
              <span className="flex-1 h-px bg-slate-200" />
              SECURE
              <span className="flex-1 h-px bg-slate-200" />
            </div>

            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              By continuing, you agree to our Terms & Privacy. We&apos;ll use your Google name &amp; email only to identify your agent and route payouts.
            </p>

            <Link
              to="/"
              data-testid="login-back-link"
              className="mt-6 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              ← Back to homepage <ArrowRight size={14} className="opacity-0 -ml-1" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Bullet({ icon: Icon, text }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 grid place-items-center w-6 h-6 rounded-full bg-blue-600 text-white shrink-0">
        <Icon size={12} />
      </span>
      <span className="text-slate-700">{text}</span>
    </li>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.185l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}
